import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, inr } from "../lib/api";
import { useAuth } from "../lib/auth";
import Shell, { PageHeader } from "../components/Shell";
import NewSkuModal from "../components/NewSkuModal";
import { Plus, Trash, MagnifyingGlass, ArrowLeft, Warning, Truck } from "@phosphor-icons/react";

const EWAY_THRESHOLD = 50000;

export default function NewDispatch() {
  const { stores } = useAuth();
  const nav = useNavigate();
  const [storeId, setStoreId] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [lines, setLines] = useState([]);
  const [eway, setEway] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [transport, setTransport] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [seller, setSeller] = useState(null);
  const [showNewSku, setShowNewSku] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => { if (!storeId && stores.length > 0) setStoreId(stores[0].id); }, [stores]);
  useEffect(() => {
    api.get("/settings/company").then((r) => setSeller(r.data)).catch(() => setSeller(null));
  }, []);

  const search = async () => {
    if (!q) { setResults([]); setSearched(false); return; }
    const r = await api.get(`/warehouse/skus?q=${encodeURIComponent(q)}&limit=10`);
    setResults(r.data.items || []);
    setSearched(true);
  };

  const add = (s) => {
    if (lines.find((l) => l.sku === s.sku)) return;
    setLines([...lines, {
      sku: s.sku, product_name: s.name, barcode: s.barcode || "", hsn: s.hsn || "",
      gst_rate: s.gst_rate || 0, qty: 1, rate: s.selling_price || s.mrp || s.cost_price || 0, thumbnail: s.thumbnail || ""
    }]);
    setResults([]); setQ(""); setSearched(false);
  };

  const updateLine = (i, k, v) => {
    const copy = [...lines];
    copy[i] = { ...copy[i], [k]: k === "qty" || k === "rate" || k === "gst_rate" ? parseFloat(v) || 0 : v };
    setLines(copy);
  };
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const store = useMemo(() => stores.find((s) => s.id === storeId), [stores, storeId]);
  const isInterstate = useMemo(() => {
    if (!store || !seller) return false;
    return String(store.state_code || "").trim() !== String(seller.state_code || "").trim();
  }, [store, seller]);

  const totals = useMemo(() => {
    let sub = 0, cgst = 0, sgst = 0, igst = 0;
    for (const l of lines) {
      const taxable = (l.qty || 0) * (l.rate || 0);
      const tax = taxable * (l.gst_rate || 0) / 100;
      sub += taxable;
      if (isInterstate) igst += tax; else { cgst += tax / 2; sgst += tax / 2; }
    }
    return { sub, cgst, sgst, igst, grand: sub + cgst + sgst + igst };
  }, [lines, isInterstate]);

  const missingHsn = useMemo(() => lines.filter((l) => !(l.hsn || "").trim()), [lines]);
  const ewbRequired = totals.grand > EWAY_THRESHOLD;
  const ewbWarning = ewbRequired && !eway.trim();

  const submit = async () => {
    if (!storeId || lines.length === 0) { alert("Pick a store and add items."); return; }
    if (missingHsn.length > 0) {
      alert(`HSN code is required for every line.\n\n${missingHsn.length} line(s) are missing HSN. Please fill the HSN column or update the SKU master.`);
      return;
    }
    if (ewbWarning) {
      const proceed = window.confirm(
        `This Delivery Challan is ${inr(totals.grand)} which exceeds ₹50,000.\n\n` +
        `An E-Way Bill is mandatory under GST rules. You have not entered an E-Way Bill number yet.\n\n` +
        `Click OK to dispatch WITHOUT an EWB (you can add it later by editing the challan). Click Cancel to enter the EWB first.`
      );
      if (!proceed) return;
    }
    setSaving(true);
    try {
      await api.post("/warehouse/dispatches", {
        store_id: storeId, items: lines, eway_bill_no: eway,
        vehicle_no: vehicle, transport_name: transport, notes,
      });
      nav("/warehouse/dispatches");
    } catch (e) {
      alert("Failed: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const canSubmit = !saving && lines.length > 0 && missingHsn.length === 0;

  return (
    <Shell>
      <PageHeader
        title="New Delivery Challan"
        subtitle="Send inventory from warehouse to a store. A GST Delivery Challan is auto-generated."
        testId="new-dispatch-header"
        actions={<button onClick={() => nav("/warehouse/dispatches")} className="inline-flex items-center gap-1 h-10 px-4 border border-slate-300 text-sm font-semibold rounded-md hover:bg-slate-100"><ArrowLeft size={14} /> Back</button>}
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {seller && (
            <div className="bg-slate-900 text-white rounded-md p-4 text-xs flex items-start justify-between gap-4" data-testid="seller-banner">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Dispatching from</div>
                <div className="font-display font-bold text-sm mt-0.5">{seller.name}</div>
                <div className="text-slate-300 mt-0.5">{seller.address}</div>
                <div className="text-slate-400 mt-1 font-mono-num">GSTIN: {seller.gstin} · {seller.state} ({seller.state_code})</div>
              </div>
              <button onClick={() => nav("/settings/company")} className="text-[10px] uppercase tracking-widest text-slate-300 hover:text-white font-semibold shrink-0">Edit ↗</button>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-md p-5">
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Dispatch to Store</label>
            <select value={storeId} onChange={(e) => setStoreId(e.target.value)} data-testid="dispatch-store-select" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900">
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.code} · {s.state} ({s.state_code})</option>)}
            </select>
            {store && seller && (
              <div className={`mt-2 text-[11px] inline-flex items-center gap-1.5 font-semibold ${isInterstate ? "text-amber-700" : "text-emerald-700"}`}>
                {isInterstate ? "Interstate · IGST applies" : "Intrastate · CGST + SGST applies"}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Add Product</div>
              <button onClick={() => setShowNewSku(true)} data-testid="open-new-sku" className="inline-flex items-center gap-1 h-8 px-3 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs font-bold rounded-md"><Plus size={12} weight="bold" /> New SKU</button>
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="dispatch-product-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())}
                  placeholder="Search SKU / name / barcode…"
                  className="w-full h-11 pl-9 pr-3 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <button onClick={search} className="h-11 px-4 bg-slate-900 text-white font-semibold rounded-md text-sm">Search</button>
            </div>
            {results.length > 0 && (
              <div className="mt-3 border border-slate-200 rounded-md divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {results.map((s) => (
                  <button key={s.sku} onClick={() => add(s)} data-testid={`add-result-${s.sku}`} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-3">
                    {s.thumbnail ? <img src={s.thumbnail} alt="" className="w-8 h-8 rounded-sm object-cover" /> : <div className="w-8 h-8 rounded-sm bg-slate-100" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{s.name}</div>
                      <div className="text-xs text-slate-500 font-mono-num">{s.sku} · HSN {s.hsn || "—"} · {s.barcode}</div>
                    </div>
                    <Plus size={14} className="text-emerald-600" />
                  </button>
                ))}
              </div>
            )}
            {searched && results.length === 0 && q && (
              <button onClick={() => setShowNewSku(true)} data-testid="no-sku-results" className="mt-3 w-full text-left px-3 py-2 border border-dashed border-emerald-300 rounded-md text-sm text-emerald-700 hover:bg-emerald-50">
                No SKU found. Create "<span className="font-mono-num font-semibold">{q}</span>" as a new SKU →
              </button>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Items ({lines.length})</div>
              {missingHsn.length > 0 && (
                <div className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600" data-testid="missing-hsn-warning">
                  <Warning size={12} weight="fill" />{missingHsn.length} line(s) missing HSN
                </div>
              )}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Product</th>
                  <th className="text-left px-3 py-2 font-semibold">HSN <span className="text-red-500">*</span></th>
                  <th className="text-right px-3 py-2 font-semibold">GST%</th>
                  <th className="text-right px-3 py-2 font-semibold">Qty</th>
                  <th className="text-right px-3 py-2 font-semibold">Rate</th>
                  <th className="text-right px-3 py-2 font-semibold">Taxable</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">Search products above to add to the Delivery Challan.</td></tr>}
                {lines.map((l, i) => {
                  const blankHsn = !(l.hsn || "").trim();
                  return (
                    <tr key={l.sku} className={`border-b border-slate-100 ${blankHsn ? "bg-red-50/50" : ""}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{l.product_name}</div>
                        <div className="text-[10px] text-slate-500 font-mono-num">{l.sku}</div>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          value={l.hsn}
                          onChange={(e) => updateLine(i, "hsn", e.target.value)}
                          placeholder="HSN"
                          data-testid={`line-hsn-${i}`}
                          className={`w-20 h-8 px-2 border rounded-md text-xs font-mono-num ${blankHsn ? "border-red-400 focus:ring-red-500 bg-red-50" : "border-slate-300"}`}
                        />
                      </td>
                      <td className="px-3 py-2"><input type="number" value={l.gst_rate} onChange={(e) => updateLine(i, "gst_rate", e.target.value)} className="w-16 h-8 px-2 border border-slate-300 rounded-md text-xs font-mono-num text-right" /></td>
                      <td className="px-3 py-2"><input type="number" value={l.qty} onChange={(e) => updateLine(i, "qty", e.target.value)} data-testid={`line-qty-${i}`} className="w-16 h-8 px-2 border border-slate-300 rounded-md text-xs font-mono-num text-right" /></td>
                      <td className="px-3 py-2"><input type="number" step="0.01" value={l.rate} onChange={(e) => updateLine(i, "rate", e.target.value)} className="w-24 h-8 px-2 border border-slate-300 rounded-md text-xs font-mono-num text-right" /></td>
                      <td className="px-3 py-2 text-right font-mono-num font-semibold">{inr((l.qty || 0) * (l.rate || 0))}</td>
                      <td className="px-3 py-2 text-right"><button onClick={() => removeLine(i)} className="p-1 text-red-600 hover:bg-red-50 rounded-md"><Trash size={14} /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-4">
          {ewbRequired && (
            <div
              className={`rounded-md p-4 text-sm border ${eway.trim() ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-300 text-amber-900"}`}
              data-testid="eway-warning-banner"
            >
              <div className="flex items-start gap-2">
                <Warning size={18} weight="fill" className="shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold">E-Way Bill Required</div>
                  <div className="text-xs mt-0.5">
                    Total <span className="font-mono-num font-bold">{inr(totals.grand)}</span> exceeds the ₹50,000 GST threshold.
                    Generate the E-Way Bill from Tally and paste the EWB number below before dispatch.
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 inline-flex items-center gap-1.5"><Truck size={12} weight="bold" /> Transport & E-Way</div>
            <input
              value={eway}
              onChange={(e) => setEway(e.target.value)}
              placeholder={ewbRequired ? "E-Way Bill Number (REQUIRED)" : "E-Way Bill Number"}
              data-testid="eway-input"
              className={`w-full h-10 px-3 border rounded-md text-sm font-mono-num focus:outline-none ${ewbRequired && !eway.trim() ? "border-amber-400 focus:ring-2 focus:ring-amber-500" : "border-slate-300 focus:ring-2 focus:ring-slate-900"}`}
            />
            <input value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="Vehicle No." className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
            <input value={transport} onChange={(e) => setTransport(e.target.value)} placeholder="Transporter" className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm" />
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" rows={2} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-5 space-y-2">
            <div className="flex items-center justify-between text-sm"><span className="text-slate-500">Sub total</span><span className="font-mono-num">{inr(totals.sub)}</span></div>
            {isInterstate ? (
              <div className="flex items-center justify-between text-sm"><span className="text-slate-500">IGST</span><span className="font-mono-num">{inr(totals.igst)}</span></div>
            ) : (
              <>
                <div className="flex items-center justify-between text-sm"><span className="text-slate-500">CGST</span><span className="font-mono-num">{inr(totals.cgst)}</span></div>
                <div className="flex items-center justify-between text-sm"><span className="text-slate-500">SGST</span><span className="font-mono-num">{inr(totals.sgst)}</span></div>
              </>
            )}
            <div className="border-t border-slate-200 pt-2 mt-2 flex items-center justify-between">
              <span className="font-display font-bold">Grand Total</span>
              <span className="font-display font-bold text-xl font-mono-num" data-testid="dispatch-grand-total">{inr(totals.grand)}</span>
            </div>
            <div className="text-[10px] text-slate-400">{isInterstate ? "Interstate supply — IGST charged at line GST%." : "Intrastate supply — GST split equally into CGST + SGST."}</div>
          </div>

          <button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="submit-dispatch-btn"
            className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-md text-sm disabled:opacity-50"
          >
            {saving ? "Creating…" : "Create Delivery Challan"}
          </button>
        </div>
      </div>
      {showNewSku && (
        <NewSkuModal
          initialSku={q}
          ctaLabel="Create & Add to Challan"
          subtitle="Added to the SKU Master and to this Delivery Challan in one step."
          onClose={() => setShowNewSku(false)}
          onCreated={(s) => { setShowNewSku(false); add(s); }}
        />
      )}
    </Shell>
  );
}
